import type { AnchorVisibleWindow } from '../utils/anchorWindow'
import type { ResetZoomPolicy } from './resetZoom'

/**
 * Per-user, global interaction feel — mouse wheel / drag grammar shared by
 * EVERY chart on screen. Deliberately NOT part of `ChartSettings`: that type
 * is per-chart (or per-grid-panel) and persists on the workspace panel
 * record, so a key living there would let one panel's stale value diverge
 * from the user's global choice. See
 * docs/superpowers/specs/2026-07-31-chart-interaction-profiles-design.md (D1).
 */
export interface ChartInteractionSettings {
  /** Mouse wheel over the chart body. 'zoom' = scroll-to-zoom grammar; 'pan' = Thinkorswim
   *  (wheel-up scrolls left / back in time). Ctrl+wheel: in 'pan' mode it zooms
   *  (respecting `zoomAnchor`); in 'zoom' mode it keeps the pre-existing
   *  cursor-anchored zoom override (temporarily anchors at the pointer even
   *  when `zoomAnchor` is 'lastBar') rather than panning — decided 2026-07-31. */
  wheelAction: 'zoom' | 'pan'
  /** Click-drag on the chart body. 'pan' = scroll-to-zoom grammar; 'zoomRegion' = Thinkorswim
   *  rubber-band select-and-zoom. */
  dragAction: 'pan' | 'zoomRegion'
  /** Wheel-zoom anchor — MOVED here from ChartSettings (dev-only app, no migration;
   *  old per-panel values simply stop being read). */
  zoomAnchor: 'cursor' | 'lastBar'
  /** Global "history time range": the visible window a REGULAR chart auto-applies
   *  whenever it enters history mode (anchored to a past date). Set once, honored
   *  by every chart. Absent ⇒ the full extended session (the historical default).
   *  Lives here (global per-user) rather than on a panel record so one chart's
   *  stale value can't diverge from the user's choice — same rationale as the
   *  grammar fields. Interaction PRESETS never touch it (see applyPreset). */
  historyVisibleWindow?: AnchorVisibleWindow
  /** Draw horizontal lines at in-play warrant exercise prices ("dilution
   *  levels") on every chart. OFF by default. Global per-user like the other
   *  fields — a stale per-panel copy could diverge from the user's choice.
   *  Not a grammar field, so INTERACTION_PRESETS never carry it (they merge,
   *  preserving this toggle) and `interactionEquals` ignores it. */
  dilutionLevels?: boolean
  /** When switching the chart's ticker, reset the horizontal zoom the way the
   *  reset gesture does (the `resetZoom` policy below for the chart's timeframe
   *  class) instead of carrying the previous ticker's zoom over to the new
   *  symbol. OFF by default: the in-place symbol swap deliberately preserves
   *  zoom (Chris, 2026-07-30), and that stays the default. Honored on live and
   *  anchored (History) charts alike. Global per-user like the other fields — a stale
   *  per-panel copy could diverge from the user's choice. Not a grammar field,
   *  so INTERACTION_PRESETS never carry it (they merge, preserving this toggle)
   *  and `interactionEquals` ignores it. */
  resetZoomOnSymbolChange?: boolean
  /** What the reset gesture (body double-click, context-menu Reset zoom, and
   *  the reset-on-ticker-change opt-in above) shows, chosen per timeframe
   *  class: a zoom level or a range such as "from session open" or "10 days
   *  back". Consulted ONLY inside `resetTimeScale()`; timeframe switches and
   *  first paint keep their current behavior (spec D1). Absent, or an absent
   *  row, means today's fixed reset. Global per-user like the other fields,
   *  for the same reason. Not a grammar field, so INTERACTION_PRESETS never
   *  carry it (they merge, preserving it) and `interactionEquals` ignores it.
   *  See docs/superpowers/specs/2026-09-22-chart-reset-zoom-defaults-design.md. */
  resetZoom?: ResetZoomPolicy
  /** Smooth motion: the wheel zoom glides to its new level instead of jumping,
   *  Go to latest glides back instead of jumping, and the price scale eases to
   *  its new fit during both instead of snapping. A drag still stops exactly
   *  where it is let go. ON by default; absent (settings saved before this existed) also means on.
   *  Global per-user like the other fields. Not a grammar field, so
   *  INTERACTION_PRESETS never carry it (they merge, preserving it) and
   *  `interactionEquals` ignores it. */
  smoothMotion?: boolean
}

export const DEFAULT_INTERACTION_SETTINGS: ChartInteractionSettings = {
  wheelAction: 'zoom',
  dragAction: 'pan',
  zoomAnchor: 'lastBar', // current app default — the scroll-to-zoom preset IS the default
  dilutionLevels: false,
  resetZoomOnSymbolChange: false,
  smoothMotion: true,
}

export interface InteractionPreset {
  id: string
  name: string
  settings: ChartInteractionSettings
}

/**
 * Preset names deliberately describe the GESTURE, not the platform whose
 * grammar they match (trademark caution — competitor names stay out of the
 * UI). Support mapping, for "coming from X?" conversations:
 *   - "Scroll to zoom"  ⇒ the scroll-to-zoom grammar (also Webull and many
 *     web-embedded charting platforms)
 *   - "Drag to zoom"    ⇒ the Thinkorswim grammar
 *   - "Scroll to pan"   ⇒ the classic desktop grammar (MetaTrader,
 *     NinjaTrader, Sierra Chart, IBKR TWS)
 */
export const INTERACTION_PRESETS: InteractionPreset[] = [
  {
    id: 'scroll-zoom',
    name: 'Scroll to zoom',
    settings: { wheelAction: 'zoom', dragAction: 'pan', zoomAnchor: 'lastBar' },
  },
  {
    id: 'scroll-pan',
    name: 'Scroll to pan',
    settings: { wheelAction: 'pan', dragAction: 'pan', zoomAnchor: 'lastBar' },
  },
]
